/* eslint func-style: "off" */
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import moment from 'moment-timezone';
import get from 'lodash/get';
import apiClient from 'helpers/apiClient';
import { actions as appUserActions } from 'containers/AppUser';
import {
  LOAD_USER, LOAD_TIMEZONES, SAVE_USER, VERIFY_PHONE, CHECK_PHONE,
  loadUserSucceeded, loadUserFailed, loadTimezonesSucceeded,
  saveUserSucceeded, saveUserFailed, verifyPhone, verifyPhoneSucceeded, verifyPhoneFailed,
  checkPhoneSucceeded, checkPhoneFailed,
} from './UserForm.ducks';

const errorMessage = (e, fallback) => get(e, 'body.error') || fallback;
const prettyName = (u = {}) => `${u.first_name || ''} ${u.last_name || ''}`.trim();

// The Backbone My Settings form saved through Account.get('user'), so the legacy
// side saw every change immediately. Keep that true for widgets that still read it.
// The bootstrap user's timezone_offset: seconds behind UTC.
// 0 - x rather than x * -60, so UTC yields 0 and not -0.
export const timezoneOffset = (timezone) => 0 - moment().tz(timezone || 'UTC').utcOffset() * 60;

export function syncLegacyAccountUser(user) {
  if (typeof Account === 'undefined' || !Account || typeof Account.get !== 'function') { return; }
  const model = Account.get('user');
  if (!model || typeof model.set !== 'function') { return; }
  model.set({ ...user, timezone_offset: timezoneOffset(user.timezone) });
}

const loadUserSaga = function* (action) {
  try {
    const user = yield call(apiClient, `/api/users/${action.payload}`);
    yield put(loadUserSucceeded(user));
  } catch (e) {
    yield put(loadUserFailed(errorMessage(e, 'There was a problem loading this user.')));
  }
};

const loadTimezonesSaga = function* () {
  try {
    const list = yield call(apiClient, '/api/utilities/timezones');
    yield put(loadTimezonesSucceeded(list));
  } catch (e) {
    yield put(loadTimezonesSucceeded([]));
  }
};

const saveUserSaga = function* (action) {
  const { payload, isNew, accountId, screen, phoneChanged, previousVerified, createAnother } = action.payload;
  try {
    const response = isNew
      ? yield call(apiClient, `/api/accounts/${accountId}/users`, { method: 'POST', body: JSON.stringify(payload) })
      : yield call(apiClient, `/api/users/${payload.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    // Bacon's user document carries no phone fields on PUT/POST; keep what we sent,
    // or the stored number when the payload left an unchanged phone out.
    const phone = isNew || payload.phone !== undefined
      ? payload.phone
      : yield select((state) => get(state, 'UserForm.user.phone') || '');
    const user = {
      ...payload,
      ...response,
      phone: phone || '',
      phone_number_verified: !phoneChanged && previousVerified === true ? true : null,
    };
    // The password went to Bacon once; it must not live on in Redux, Account or LogRocket.
    delete user.password;
    delete user.confirm_password;
    yield put(saveUserSucceeded({ user, isNew, createAnother }));
    if (screen === 'mySettings') {
      const appUser = { ...user, timezone_offset: timezoneOffset(user.timezone) };
      yield put(appUserActions.updateUser(appUser));
      yield call(syncLegacyAccountUser, appUser);
      if (phoneChanged && user.phone) {
        yield put(verifyPhone(user.phone));
      }
    }
  } catch (e) {
    const verb = isNew ? 'creating' : 'updating';
    yield put(saveUserFailed(errorMessage(e, `There was a problem ${verb} ${prettyName(payload)}. Please try again later.`)));
  }
};

const verifyPhoneSaga = function* (action) {
  try {
    yield call(apiClient, '/api/users/verify-phone', { method: 'POST', body: JSON.stringify({ phone: action.payload }) });
    yield put(verifyPhoneSucceeded());
  } catch (e) {
    yield put(verifyPhoneFailed(errorMessage(e, 'We could not send a verification code. Please try again.')));
  }
};

const checkPhoneSaga = function* (action) {
  try {
    yield call(apiClient, '/api/users/check-phone', { method: 'POST', body: JSON.stringify(action.payload) });
    yield put(checkPhoneSucceeded());
    const user = yield select((state) => state.AppUser.user);
    const verifiedUser = { ...user, phone_number_verified: true };
    yield put(appUserActions.updateUser(verifiedUser));
    yield call(syncLegacyAccountUser, verifiedUser);
  } catch (e) {
    yield put(checkPhoneFailed(errorMessage(e, 'Invalid or expired verification code')));
  }
};

const watchLoadUser = function* () { yield takeLatest(LOAD_USER, loadUserSaga); };
const watchLoadTimezones = function* () { yield takeLatest(LOAD_TIMEZONES, loadTimezonesSaga); };
const watchSaveUser = function* () { yield takeLatest(SAVE_USER, saveUserSaga); };
const watchVerifyPhone = function* () { yield takeLatest(VERIFY_PHONE, verifyPhoneSaga); };
const watchCheckPhone = function* () { yield takeLatest(CHECK_PHONE, checkPhoneSaga); };

const sagas = function* () {
  yield all([fork(watchLoadUser), fork(watchLoadTimezones), fork(watchSaveUser), fork(watchVerifyPhone), fork(watchCheckPhone)]);
};

export { loadUserSaga, loadTimezonesSaga, saveUserSaga, verifyPhoneSaga, checkPhoneSaga,
  watchLoadUser, watchLoadTimezones, watchSaveUser, watchVerifyPhone, watchCheckPhone };
export default sagas;
